/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Loader2, AlertTriangle, ArrowRight, ClipboardList, Check, Copy, ShieldCheck } from "lucide-react";

import { T } from "../../lib/i18n";
import { useAiModels } from "../../lib/aiModels";
// 层级：这些 AI 弹窗也会在设计项目抽屉（ProjectDrawer z-[70]）内打开，z-[60] 会被抽屉盖住 → 抬到 z-[95]
const Shell = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
    <div className="bg-white w-full max-w-2xl overflow-hidden flex flex-col rounded-2xl max-h-[88dvh]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-violet-50 flex-wrap md:flex-nowrap">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-indigo-600" />{title}</p>
        <button onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
      </div>
      <div className="p-5 overflow-y-auto">{children}</div>
    </div>
  </div>
);

const Spinner = ({ zh }) => {
  const aiModels = useAiModels();
  return (
    <div className="flex-wrap md:flex-nowrap flex items-center justify-center gap-2 py-10 text-slate-500 text-sm"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" />{zh ? `${aiModels.extract || "AI"} 分析中…` : "Analyzing…"}</div>
  );
};

export function AdvisorModal({ pid, api, zh, lang, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.post(`/design/projects/${pid}/ai/advisor?lang=${lang}`).then(({ data }) => setD(data)).catch(() => setD({ summary: zh ? "分析失败" : "Failed", risks: [], nextActions: [] })); }, [api, pid, lang, zh]);
  return (
    <Shell title={zh ? "AI 项目诊断与下一步" : "AI Project Advisor"} onClose={onClose} testid="design-ai-advisor-modal">
      {!d ? <Spinner zh={zh} /> : (
        <div className="space-y-4" data-testid="design-ai-advisor-content">
          <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{T(d.summary)}</p>
          {d.risks?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mb-1.5"><AlertTriangle className="h-3.5 w-3.5" />{zh ? "关键风险 / 卡点" : "Risks"}</p>
              <ul className="space-y-1">{d.risks.map((r, i) => <li key={i} className="text-[13px] text-slate-600 flex gap-1.5"><span className="text-red-400">•</span>{r}</li>)}</ul>
            </div>
          )}
          {d.nextActions?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-indigo-600 flex items-center gap-1 mb-1.5"><ArrowRight className="h-3.5 w-3.5" />{zh ? "建议下一步" : "Next Actions"}</p>
              <div className="space-y-2">{d.nextActions.map((a, i) => (
                <div key={i} className="rounded-lg border border-slate-100 p-2.5" data-testid={`design-ai-action-${i}`}>
                  <p className="text-[13px] text-slate-700">{T(a.action)}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{zh ? "建议指派" : "Assign"}: <span className="text-indigo-600 font-medium">{a.suggestedAssignee}</span>{a.why ? ` · ${T(a.why)}` : ""}</p>
                </div>
              ))}</div>
            </div>
          )}
          <p className="text-[10px] text-slate-400">{zh ? "AI 建议仅供参考，请结合实际判断。" : "AI suggestions are advisory."}</p>
        </div>
      )}
    </Shell>
  );
}

export function AgencyAdviceModal({ pid, api, zh, lang, agencies, onApply, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.post(`/design/projects/${pid}/ai/agency-advice?lang=${lang}`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([])); }, [api, pid, lang]);
  const badge = (v) => v === "Required" ? "bg-red-100 text-red-700" : v === "Exempt/Not Applicable" ? "bg-slate-100 text-slate-500" : v === "Potentially Required" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400";
  const apply = async (it) => {
    const a = (agencies || []).find((x) => x.type === it.type);
    if (!a) return toast.error(zh ? "未匹配到该审批" : "No match");
    await onApply(a.id, it.likely, it.basis);
    toast.success(zh ? "已应用到专项审批" : "Applied");
  };
  return (
    <Shell title={zh ? "AI 专项审批研判" : "AI Agency Advice"} onClose={onClose} testid="design-ai-agency-modal">
      {!items ? <Spinner zh={zh} /> : items.length === 0 ? <p className="text-slate-400 text-sm text-center py-6">{zh ? "无建议" : "No advice"}</p> : (
        <div className="space-y-1.5" data-testid="design-ai-agency-content">
          {items.map((it, i) => (
            <div key={i} className="flex-wrap md:flex-nowrap flex items-start gap-2 rounded-lg border border-slate-100 p-2.5" data-testid={`design-ai-agency-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-slate-700">{it.type}</p>
                <p className="text-[11px] text-slate-400">{it.basis}</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${badge(it.likely)}`}>{it.likely}</span>
              {onApply && <button onClick={() => apply(it)} title={zh ? "应用" : "Apply"} data-testid={`design-ai-agency-apply-${i}`} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-indigo-500 hover:text-indigo-700 shrink-0"><Check className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

export function OwnerPortalModal({ pid, api, zh, onClose }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/design/projects/${pid}/owner-portal`).then(({ data }) => setD(data)).catch(() => setD({ token: null, published: [], signatures: [], markups: [] }));
  useEffect(() => { load(); }, [pid]);
  const enable = async () => { await api.post(`/design/projects/${pid}/owner-portal`); load(); };
  const rotate = async () => { if (!window.confirm(zh ? "重置后旧链接立即失效，确定？" : "Old link will stop working. Continue?")) return; await api.post(`/design/projects/${pid}/owner-portal/rotate`); toast.success(zh ? "已重置链接" : "Link rotated"); load(); };
  const link = d?.token ? `${window.location.origin}/owner/design/${d.token}` : "";
  const copy = () => { navigator.clipboard?.writeText(link); toast.success(zh ? "链接已复制" : "Copied"); };
  return (
    <Shell title={zh ? "业主门户与电子签署" : "Owner Portal & E-Sign"} onClose={onClose} testid="design-owner-portal-modal">
      {!d ? <Spinner zh={zh} /> : (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-xs font-semibold text-slate-600 mb-1">{zh ? "业主专属链接" : "Owner link"}</p>
            {d.token ? (
              <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
                <input readOnly value={link} data-testid="owner-portal-link" className="flex-1 text-[11px] rounded border border-slate-200 px-2 py-1.5 bg-slate-50" />
                <button onClick={copy} data-testid="owner-portal-copy" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[12px] px-2.5 py-1.5 rounded bg-indigo-600 text-white flex items-center gap-1"><Copy className="h-3.5 w-3.5" />{zh ? "复制" : "Copy"}</button>
                <button onClick={rotate} data-testid="owner-portal-rotate" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[12px] px-2.5 py-1.5 rounded bg-slate-100 text-slate-600 hover:text-red-600">{zh ? "重置" : "Rotate"}</button>
              </div>
            ) : (
              <button onClick={enable} data-testid="owner-portal-enable" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[12px] px-3 py-1.5 rounded bg-indigo-600 text-white">{zh ? "生成业主链接" : "Enable portal"}</button>
            )}
            <p className="text-[10px] text-slate-400 mt-1.5">{zh ? `仅展示「已批准 Approved」的方案版本（当前 ${d.published.length} 个）。业主可批注并电子签署确认。` : `Shows Approved plan versions only (${d.published.length}).`}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />{zh ? "签署存证" : "Signatures"} ({d.signatures.length})</p>
            {d.signatures.length === 0 ? <p className="text-[11px] text-slate-400">{zh ? "业主尚未签署" : "No signatures yet"}</p> : (
              <div className="space-y-1.5" data-testid="owner-portal-signatures">
                {d.signatures.map((s) => (
                  <div key={s.id} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-2.5 text-[11px] text-slate-600" data-testid={`owner-sig-${s.id}`}>
                    <p className="text-[13px] text-slate-800 font-medium">{s.signerName} · {s.fileType} v{s.fileVersion}</p>
                    <p className="mt-0.5">{s.at?.slice(0, 19).replace("T", " ")} UTC · IP {s.ip}</p>
                    <p className="font-mono text-[9px] break-all">SHA256 {s.fileSha256}</p>
                    <p className="font-mono text-[9px] break-all">{zh ? "存证" : "Record"} {s.recordHash}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {d.markups.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1.5">{zh ? "业主批注" : "Owner markups"} ({d.markups.length})</p>
              <div className="space-y-1" data-testid="owner-portal-admin-markups">
                {d.markups.map((m, i) => <p key={m.id} className="text-[12px] text-slate-600"><span className="text-red-500">#{i + 1}</span> {T(m.text)}</p>)}
              </div>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}


export function CityParseModal({ pid, api, zh, lang, stages, onCreated, onClose }) {
  const [text, setText] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [stageKey, setStageKey] = useState("");
  const run = async () => {
    if (!text.trim()) return toast.error(zh ? "请粘贴 City 批注" : "Paste comments");
    setLoading(true);
    try { const { data } = await api.post(`/design/projects/${pid}/ai/parse-city-comments?lang=${lang}`, { text }); setItems(data.items || []); }
    catch (e) { toast.error(e?.detailText || "failed"); } finally { setLoading(false); }
  };
  const createTasks = async () => {
    if (!items || items.length === 0) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/design/projects/${pid}/tasks/from-comments`, { items, stageKey: stageKey || undefined });
      toast.success(zh ? `已生成 ${data.created.length} 个任务并指派` : `Created ${data.created.length} tasks`);
      onCreated && (await onCreated());
      onClose();
    } catch (e) { toast.error(e?.detailText || "failed"); } finally { setCreating(false); }
  };
  return (
    <Shell title={zh ? "AI City 批注解析" : "AI City Comment Parser"} onClose={onClose} testid="design-ai-city-modal">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder={zh ? "粘贴 City 的 correction / comment 意见文本…" : "Paste city correction letter…"} data-testid="design-ai-city-input" className="w-full text-sm rounded-lg border border-slate-200 p-2.5 mb-2" />
      <button onClick={run} disabled={loading} data-testid="design-ai-city-run" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white disabled:opacity-50 flex items-center gap-1.5">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}{zh ? "拆解为待办项" : "Parse to items"}</button>
      {items && (
        <div className="mt-4 space-y-1.5" data-testid="design-ai-city-content">
          {items.length === 0 ? <p className="text-slate-400 text-sm">{zh ? "未解析出待办" : "No items"}</p> : items.map((it, i) => (
            <div key={i} className="rounded-lg border border-slate-100 p-2.5" data-testid={`design-ai-city-item-${i}`}>
              <p className="text-[13px] text-slate-700">{it.item}</p>
              <p className="text-[11px] text-slate-400 mt-0.5"><span className="px-1.5 py-0.5 bg-slate-100 rounded">{T(it.category)}</span> · {zh ? "建议" : "role"}: {it.suggestedRole}</p>
            </div>
          ))}
          {items.length > 0 && (
            <div className="flex-wrap md:flex-nowrap flex items-center gap-2 pt-2 border-t border-slate-100 mt-2">
              <span className="text-[11px] text-slate-500">{zh ? "生成到阶段" : "Into stage"}:</span>
              <select value={stageKey} onChange={(e) => setStageKey(e.target.value)} data-testid="design-ai-city-stage" className="text-[11px] rounded border border-slate-200 px-1.5 py-1 bg-white flex-1">
                <option value="">{zh ? "当前阶段(默认)" : "Current stage"}</option>
                {(stages || []).map((s) => <option key={s.id} value={s.key}>{T(s.name)}</option>)}
              </select>
              <button onClick={createTasks} disabled={creating} data-testid="design-ai-city-create" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-3 py-1.5 text-[12px] rounded-lg bg-emerald-600 text-white disabled:opacity-50 flex items-center gap-1.5">{creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}{zh ? "一键生成任务并指派" : "Create & assign tasks"}</button>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}
